import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useSpotlightActions } from '@lumapps/lumx-spotlight/hooks/useSpotlightActions';
import type { NavigationPage } from '@lumapps/lumx-spotlight/types';
import {
    SideNavigationItem as NavigationItem,
    SideNavigationItemProps as NavigationProps,
    Emphasis,
} from '@lumapps/lumx/react';
import { useRouteMatching } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useDynamicIcon } from '@lumapps/utils/hooks/useDynamicIcon';

import { useSideBarNavigationDataAttributes } from '../../../hooks/useSideBarNavigationDataAttributes';
import { type SideNavRoute, extractRoutes } from '../../../utils';

export interface SideNavigationMenuProps extends Omit<NavigationProps, 'toggleButtonProps'> {
    label: string;
    baseClassName: string;
    dynamicIcon?: string;
    id?: string;
    sectionLabel?: string;
}

/**
 * Side Navigation Menu that displays a section with multiple links
 */
const SideNavigationMenu: React.FC<SideNavigationMenuProps> = ({
    baseClassName,
    children,
    isOpen = false,
    dynamicIcon = '',
    id = '',
    icon,
    sectionLabel,
    ...otherProps
}) => {
    const { translateKey } = useTranslate();
    const { element } = useClassnames(baseClassName);
    const { registerPage, unregisterPage } = useSpotlightActions();
    /**
     * In order to determine whether the menu should be open or not, we need to check whether
     * any of the children routes is the current one.
     */
    const routes = React.useMemo(() => extractRoutes(children), [children]);

    // Register all child routes with Spotlight so they are searchable even when this menu is closed
    // (and its children therefore unmounted by the DS). extractRoutes traverses the JSX tree statically.
    //
    // parentLabels: each route carries the intermediate ancestor labels collected by extractRoutes
    // (sub-menus between this menu and the route). This menu's own label is prepended here to form
    // the full ancestor chain, e.g. ['Directories', 'Electronics'] for a route nested two levels deep.
    //
    // ID namespacing: the ID uses `menuLabel:route.path` when no explicit id prop is set, so that a
    // parent menu (level N) and a nested menu (level N+1) register the same route under distinct keys.
    // Combined with SpotlightContext's reference counting, each menu can safely clean up its own
    // registrations on unmount without removing those made by a parent menu at a higher level.
    React.useEffect(() => {
        routes.forEach(
            ({ route, label: itemLabel, icon: itemIcon, id: itemId, pageKeys, parentLabels: intermediateLabels }) => {
                const navigationItem: NavigationPage = {
                    id: itemId || `${otherProps.label}:${route.path}`,
                    label: itemLabel,
                    route,
                    icon: itemIcon,
                    sectionLabel,
                    parentLabels: [otherProps.label, ...intermediateLabels],
                    parentIcon: icon,
                    pageKeys,
                };
                registerPage(navigationItem);
            },
        );

        return () => {
            routes.forEach(({ id: itemId, route }) => {
                unregisterPage(itemId || `${otherProps.label}:${route.path}`);
            });
        };
    }, [routes, otherProps.label, sectionLabel, registerPage, unregisterPage, icon]);

    const dataAttributes = useSideBarNavigationDataAttributes({ alternativeId: id });
    const { iconPath } = useDynamicIcon({ icon: dynamicIcon });
    const iconToUse = dynamicIcon ? iconPath : icon || undefined;

    const doesMatchAnyChildRoute = routes.reduce(
        (matches: boolean, { route, pathMatchForHighlighting }: SideNavRoute) =>
            /**
             * Since the size of the routes is always fixed (since they are defined statically in a config file)
             * we can iterate and execute a hook. Normally we would not do it, but in this case it is helpful to
             * determine if there is a child route selected.
             */
            /* eslint-disable react-hooks/rules-of-hooks */
            route ? useRouteMatching(route, pathMatchForHighlighting || `${route?.path}/*`) || matches : false,
        false,
    );

    const [isNavigationOpen, setNavigationIsOpen] = React.useState(isOpen || doesMatchAnyChildRoute);

    /**
     * With the search feature (spotlight), since we can navigate inside the app witout clicking the nav item directly
     * or realoading the page, we want to open/close menus when routes matching changes
     */
    React.useEffect(() => {
        setNavigationIsOpen(doesMatchAnyChildRoute);
    }, [doesMatchAnyChildRoute]);

    /**
     * Callback that will be executed if the navigation item has children, which means that we need
     * to open and close it rather than redirect
     */
    const onOpenClose = (event: React.MouseEvent) => {
        event.preventDefault();
        setNavigationIsOpen(!isNavigationOpen);
    };

    const className = element('subitem', [baseClassName]);

    return (
        <NavigationItem
            {...otherProps}
            className={className}
            emphasis={Emphasis.medium}
            isOpen={isNavigationOpen}
            onClick={onOpenClose}
            icon={iconToUse}
            toggleButtonProps={{ label: translateKey(GLOBAL.TOGGLE) }}
            {...dataAttributes}
        >
            {React.Children.map(children, (child: React.ReactNode) => {
                if (React.isValidElement(child)) {
                    return (
                        <child.type
                            {...child.props}
                            hasParent={Boolean(otherProps.label)}
                            sectionLabel={sectionLabel}
                        />
                    );
                }
                return child;
            })}
        </NavigationItem>
    );
};

SideNavigationMenu.displayName = 'SideNavigationItem';

export { SideNavigationMenu };
